import React, { useEffect, useState } from "react";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef, GridSortModel } from "@mui/x-data-grid";
import { useNavigate, useSearchParams } from "react-router-dom";
import ButtonComponent from "../../components/Button.Component";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import {
  deleteLinkedService,
  reactivateLinkedService,
} from "../../services/linkedService";
import { useToast } from "../../services/utils";
import { getFormattedDateTime } from "../../services/utils";
import DropdownMenu from "../../components/Molecules/DropdownMenu/DropdownMenu";
import Loader from "../../components/Molecules/Loader/Loader";
import {
  IconAudit,
  IconDeleteBlueSvg,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconEyeBase,
  IconEditBase,
  IconReactivateSvg,
} from "../../common/utils/icons";
import { enableRowGroupByColumnProps } from "../../components/DataGridProps/DataGridProps";
import { paginatedResponseFormat } from "../../services/constants";
import useFetchPaginatedAllLinkedServices from "../../hooks/useFetchPaginatedAllLinkedServices";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import { useAuth } from "../../contexts/AuthContext";
import { useAppTranslation } from "../../hooks/useAppTranslation";

const DataTableComponent: React.FC<any> = DataTable as any;
const fetchPaginatedHook: any = useFetchPaginatedAllLinkedServices;

const LINKED_SERVICES_FILTER_CONFIG = {
  DEFAULT_STATE: {
    query_name: "",
    status_filter: "Active",
  },

  // Fields to persist in URL
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    ID_FIELDS: [],
    STRING_FIELDS: ["query_name", "status_filter"],
    DATE_FIELDS: [],
  },
} as const;

const LinkedServices: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { t } = useAppTranslation();
  const canAddLinkedService = hasPermission("linked_service", "add");
  const canViewLinkedService = hasPermission("linked_service", "view");
  const canEditLinkedService = hasPermission("linked_service", "edit");
  const canDeleteLinkedService = hasPermission("linked_service", "delete");

  // states
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [fileData, setFileData] = useState<any>(paginatedResponseFormat);
  const [linkedServicesData, setLinkedServicesData] = useState<any>(
    paginatedResponseFormat
  );

  // Pagination default values and states
  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);

  // ✅ Server-side sorting state
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "name", sort: "asc" },
  ]);

  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const initialState: any = {
      ...LINKED_SERVICES_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Restore filters from URL parameters
    LINKED_SERVICES_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        initialState[field] = urlValue;
      }
    });

    return initialState;
  });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [linkedServiceToDelete, setLinkedServiceToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const currentSortBy = sortModel[0]?.field || "name";
  const currentSortOrder = sortModel[0]?.sort || "asc";

  // ✅ sortBy/sortOrder passed to hook — no duplicates
  const [fetchedLinkedServices, refetchLinkedServices] = fetchPaginatedHook({
    setIsLoading,
    page,
    pSize,
    searchFilterData,
    sortBy: currentSortBy,
    sortOrder: currentSortOrder,
  });

  // Sync filters to URL parameters (state → URL)
  useEffect(() => {
    const newParams = new URLSearchParams();

    LINKED_SERVICES_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      const value = searchFilterData[field];
      if (value !== null && value !== undefined && value !== "") {
        // Don't add default values to URL to keep it clean
        if (field === "status_filter" && value === "Active") {
          return; // Skip default status filter
        }
        newParams.set(field, String(value));
      }
    });

    // Only update URL if params have changed
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();
    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFilterData, searchParams, setSearchParams]);

  // Update filter state when URL changes (URL → state for back/forward navigation)
  useEffect(() => {
    const urlFilterState: any = {
      ...LINKED_SERVICES_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Extract filter parameters from URL
    LINKED_SERVICES_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        urlFilterState[field] = urlValue;
      }
    });

    // Only update state if URL differs from current state
    const hasChanges = LINKED_SERVICES_FILTER_CONFIG.URL_FIELDS.some(
      (field) => urlFilterState[field] !== searchFilterData[field]
    );

    if (hasChanges) {
      setSearchFilterData(urlFilterState);
    }
  }, [searchParams]);

  // Reset page to 1 when search filter changes
  useEffect(() => {
    // Reset page when status filter or query name search changes
    if (
      searchFilterData?.status_filter !== undefined ||
      searchFilterData?.query_name !== undefined
    ) {
      setPage(1);
    }
  }, [searchFilterData]);

  const handelClickEvent = () => {
    navigate("/linked-services/add");
  };

  // Handle delete linked service
  const handleDeleteLinkedService = (linkedService: any) => {
    setLinkedServiceToDelete(linkedService);
    setDeleteConfirmOpen(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!linkedServiceToDelete) return;

    try {
      setDeleteLoading(true);
      const result = await deleteLinkedService(linkedServiceToDelete.id);
      if (result && result.status === 200) {
        showToast(
          t("linkedServices.toast.deleted", {
            name: linkedServiceToDelete.name,
          }),
          "success"
        );
        setDeleteConfirmOpen(false);
        setLinkedServiceToDelete(null);
        // Update the linked services list to mark as inactive
        setLinkedServicesData((prev: any) => ({
          ...prev,
          items: prev?.items?.map((item: any) =>
            item.id === linkedServiceToDelete.id
              ? { ...item, is_active: false }
              : item
          ),
        }));
        setFileData((prev: any) => ({
          ...prev,
          items: prev?.items?.map((item: any) =>
            item.id === linkedServiceToDelete.id
              ? { ...item, is_active: false }
              : item
          ),
        }));
      }
    } catch (error: any) {
      showToast(
        error?.message || t("linkedServices.toast.failedDelete"),
        "error"
      );
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  // Handle reactivate linked service
  const handleReactivate = async (linkedService: any) => {
    try {
      setIsLoading(true);

      // Optimistically update UI
      setLinkedServicesData((prev: any) => ({
        ...prev,
        items: prev?.items?.map((item: any) =>
          item.id === linkedService.id ? { ...item, is_active: true } : item
        ),
      }));
      setFileData((prev: any) => ({
        ...prev,
        items: prev?.items?.map((item: any) =>
          item.id === linkedService.id ? { ...item, is_active: true } : item
        ),
      }));

      const result = await reactivateLinkedService(linkedService.id);

      if (result) {
        showToast(
          t("linkedServices.toast.reactivated", { name: linkedService.name }),
          "success"
        );
        // Refetch to ensure data consistency
        if (refetchLinkedServices) {
          refetchLinkedServices();
        }
      }
    } catch (error: any) {
      // Revert optimistic update on error
      setLinkedServicesData((prev: any) => ({
        ...prev,
        items: prev?.items?.map((item: any) =>
          item.id === linkedService.id ? { ...item, is_active: false } : item
        ),
      }));
      setFileData((prev: any) => ({
        ...prev,
        items: prev?.items?.map((item: any) =>
          item.id === linkedService.id ? { ...item, is_active: false } : item
        ),
      }));
      showToast(
        error?.response?.data?.message ||
          t("linkedServices.toast.failedReactivate"),
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      renderCell: () => <></>,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
    },
    {
      field: "id",
      headerName: t("linkedServices.list.columns.id"),
      minWidth: 80,
      flex: 1,
      renderCell: (params: any) => {
        return <span>{params.id}</span>;
      },
    },
    {
      field: "name",
      headerName: t("linkedServices.list.columns.name"),
      flex: 1,
      minWidth: 140,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },
    {
      field: "modified_by_user",
      headerName: t("linkedServices.list.columns.modifiedBy"),
      minWidth: 140,
      flex: 1,
      sortable: false, // backend does not support sorting on nested user object
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value?.username} />;
      },
    },
    {
      field: "updated_on",
      headerName: t("linkedServices.list.columns.updatedOn"),
      minWidth: 180,
      flex: 1,
      renderCell: (params: any) => {
        return getFormattedDateTime(params.value);
      },
    },
    {
      field: "is_active",
      headerName: t("linkedServices.list.columns.status"),
      minWidth: 100,
      flex: 1,
      sortable: false, // status toggled locally; not a sortable backend field
      renderCell: (params: any) => {
        return params.value ? (
          <Box title={t("linkedServices.list.tooltip.active")}>
            <IconCheckCircleIconGreenSvg width={21} height={21} />
          </Box>
        ) : (
          <Box title={t("linkedServices.list.tooltip.inactive")}>
            <IconCrossCircleIconSvg width={21} height={21} />
          </Box>
        );
      },
    },
    {
      field: "action",
      headerName: t("linkedServices.list.columns.action"),
      align: "right",
      headerAlign: "right",
      width: 130,
      sortable: false,
      renderCell: (params: any) => {
        const handleDelete = () => {
          handleDeleteLinkedService(params.row);
        };
        const isActive = params?.row?.is_active === true;

        return (
          <>
            {/* For active records, show all action buttons */}
            {isActive && (
              <>
                {canViewLinkedService && (
                  <Tooltip
                    title={t("linkedServices.list.tooltip.view")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => {
                        navigate(`/linked-services/view/${params.row.id}`);
                      }}
                    >
                      <IconEyeBase />
                    </IconButton>
                  </Tooltip>
                )}
                {canEditLinkedService && (
                  <Tooltip
                    title={t("linkedServices.list.tooltip.edit")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => {
                        navigate(`/linked-services/edit/${params.row.id}`);
                      }}
                    >
                      <IconEditBase />
                    </IconButton>
                  </Tooltip>
                )}
                {canViewLinkedService && (
                  <DropdownMenu
                    dropdownMenuItems={[
                      <IconButton
                        key="audit-linkedServices"
                        className="datagrid-action-btn"
                        color="inherit"
                        onClick={() =>
                          navigate(
                            `/linked-services/auditLinkedServices/${params.row.id}`,
                            {
                              state: {
                                details: {
                                  name: params.row.name,
                                  id: params.row.id,
                                },
                              },
                            }
                          )
                        }
                      >
                        <IconAudit /> {t("linkedServices.list.dropdown.audit")}
                      </IconButton>,
                      ...(canDeleteLinkedService
                        ? [
                            <IconButton
                              key="delete-linkedService"
                              className="datagrid-action-btn"
                              color="inherit"
                              onClick={() => handleDelete()}
                            >
                              <IconDeleteBlueSvg />
                              {t("linkedServices.list.dropdown.delete")}
                            </IconButton>,
                          ]
                        : []),
                    ]}
                  />
                )}
              </>
            )}

            {/* For inactive records, show ONLY Reactivate button */}
            {!isActive && canEditLinkedService && (
              <Tooltip
                title={t("linkedServices.list.tooltip.reactivate")}
                placement="top"
                arrow
              >
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() => handleReactivate(params.row)}
                >
                  <IconReactivateSvg />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  // for update linked services list data
  useEffect(() => {
    if (fetchedLinkedServices?.items) {
      setFileData(fetchedLinkedServices);
      setLinkedServicesData(fetchedLinkedServices);
    }
  }, [fetchedLinkedServices]);

  return (
    <Box>
      {(isLoading || isDownloadLoading) && (
        <Loader isLoading={isLoading || isDownloadLoading} />
      )}
      {deleteLoading && <Loader isLoading={deleteLoading} zIndex={1400} />}
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle={t("linkedServices.dialog.deleteTitle")}
        dailogDescription={t("linkedServices.dialog.deleteDescription", {
          name: linkedServiceToDelete?.name,
        })}
      />

      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              setSearchFilterData={setSearchFilterData}
              searchFilterData={searchFilterData}
              requiredFields={["query_name", "status_filter"]}
              primaryFields={["query_name", "status_filter"]}
              fieldRequirements={{
                queryNamePlaceholder: t("linkedServices.list.placeholder"),
                defaultStatusFilter: "Active", // Reset to Active status on Clear Filter
              }}
            />
          </Grid>
          {canAddLinkedService && (
            <Grid
              item
              xs="auto"
              sx={{ display: "flex", alignItems: "flex-start" }}
            >
              <ButtonComponent
                handelClickEvent={handelClickEvent}
                className="btn-orange"
              >
                <AddSharpIcon sx={{ marginRight: "4px" }} />{" "}
                {t("linkedServices.list.title")}
              </ButtonComponent>
            </Grid>
          )}
        </Grid>
      </Box>

      <FlexBetween>
        <DataTableComponent
          dataRows={fileData?.items || []}
          dataColumns={columns || []}
          loading={isLoading}
          dataListTitle={t("linkedServices.list.dataListTitle")}
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(newSortModel: 
            GridSortModel) => {
              const nextSort = newSortModel[0];
              setSortModel(
                nextSort
                ?[{field: nextSort.field,
                  sort: nextSort.sort ?? "asc"
            }]
            : [{ field: "name", sort: "asc"
            }]
          );
            setPage(1); // Reset to page 1 on sort change
            console.log("SORT EVENT FIRED", newSortModel);
          }}
          
          rowCount={fileData?.total || 0}
          rowGroupByColumnProps={enableRowGroupByColumnProps}
          paginationModel={{
            page: page - 1,
            pageSize: pSize,
          }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          singlePageMaxHeightDiff={360}
        />
      </FlexBetween>
    </Box>
  );
};

export default LinkedServices;
